import { useState, useMemo, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TablePagination,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
} from "@mui/icons-material";
import DashboardLayout from "@/components/DashboardLayout";
import * as materialherstellerApi from "@/api/materialhersteller";

// Backend returns camelCase JSON
type Manufacturer = {
  id: number;
  bezeichnung: string;
};


const Manufacturers = () => {
  const [manufacturers, setManufacturers] = useState<Manufacturer[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingManufacturer, setEditingManufacturer] = useState<Manufacturer | null>(null);
  const [formData, setFormData] = useState({
    name: "",
  });

  const [nameFilter, setNameFilter] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Load manufacturers from API
  useEffect(() => {
    loadManufacturers();
  }, []);

  const loadManufacturers = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await materialherstellerApi.getAll();

      // Ensure we have valid array data
      // Backend returns camelCase even though DTO is PascalCase
      const manufacturersData = Array.isArray(data) ? (data as any as Manufacturer[]) : [];
      setManufacturers(manufacturersData);
    } catch (err) {
      setError("Fehler beim Laden der Hersteller");
      console.error("Error loading manufacturers:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredManufacturers = useMemo(() => {
    return manufacturers.filter((manufacturer) => {
      // If no bezeichnung, filter it out
      if (!manufacturer.bezeichnung) {
        return false;
      }
      // If no filter text, include all manufacturers
      if (!nameFilter) {
        return true;
      }
      const matchesName = manufacturer.bezeichnung.toLowerCase().includes(nameFilter.toLowerCase());
      return matchesName;
    });
  }, [manufacturers, nameFilter]);

  const paginatedManufacturers = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredManufacturers.slice(start, start + rowsPerPage);
  }, [filteredManufacturers, page, rowsPerPage]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleAddNew = () => {
    setEditingManufacturer(null);
    setFormData({ name: "" });
    setIsDialogOpen(true);
  };

  const handleEdit = (manufacturer: Manufacturer) => {
    setEditingManufacturer(manufacturer);
    setFormData({
      name: manufacturer.bezeichnung ?? '',
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id: number) => {
    if (confirm("Möchten Sie diesen Hersteller wirklich löschen?")) {
      try {
        await materialherstellerApi.remove(id);
        await loadManufacturers();
      } catch (err) {
        alert("Fehler beim Löschen des Herstellers");
        console.error("Error deleting manufacturer:", err);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingManufacturer) {
        await materialherstellerApi.update(editingManufacturer.id, {
          Bezeichnung: formData.name,
        });
      } else {
        await materialherstellerApi.create({
          Bezeichnung: formData.name,
        });
      }
      setIsDialogOpen(false);
      await loadManufacturers();
    } catch (err) {
      alert("Fehler beim Speichern des Herstellers");
      console.error("Error saving manufacturer:", err);
    }
  };

  return (
    <DashboardLayout>
      <Box sx={{ maxWidth: 1400, mx: "auto" }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 4,
          }}
        >
          <Typography variant="h4" component="h1" fontWeight="bold">
            Hersteller
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddNew}
            size="large"
            sx={{ color: "white" }}
          >
            Neuer Hersteller
          </Button>
        </Box>

        {/* Filter */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Filter
          </Typography>
          <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 2 }}>
            <TextField
              label="Bezeichnung suchen"
              placeholder="Suche eingeben..."
              value={nameFilter}
              onChange={(e) => setNameFilter(e.target.value)}
              fullWidth
            />
          </Box>
        </Paper>

        {/* Table */}
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Bezeichnung</strong></TableCell>
                  <TableCell align="right"><strong>Aktionen</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={2} align="center" sx={{ py: 8 }}>
                      <Typography color="text.secondary">
                        Laden...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : error ? (
                  <TableRow>
                    <TableCell colSpan={2} align="center" sx={{ py: 8 }}>
                      <Typography color="error">
                        {error}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : paginatedManufacturers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={2} align="center" sx={{ py: 8 }}>
                      <Typography color="text.secondary">
                        {manufacturers.length === 0
                          ? "Keine Hersteller vorhanden"
                          : "Keine Hersteller entsprechen den Filterkriterien"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedManufacturers.map((manufacturer) => (
                    <TableRow key={manufacturer.id} hover>
                      <TableCell>{manufacturer.bezeichnung ?? '-'}</TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => handleEdit(manufacturer)}
                          color="primary"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(manufacturer.id)}
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filteredManufacturers.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage="Zeilen pro Seite:"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} von ${count}`
            }
          />
        </Paper>
      </Box>

      {/* Dialog */}
      <Dialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingManufacturer ? "Hersteller bearbeiten" : "Neuen Hersteller hinzufügen"}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
              <TextField
                label="Bezeichnung"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                required
                fullWidth
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsDialogOpen(false)}>Abbrechen</Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={<SaveIcon />}
              sx={{ color: "white" }}
            >
              {editingManufacturer ? "Speichern" : "Hinzufügen"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </DashboardLayout>
  );
};

export default Manufacturers;
